/**
 * keyDerivation.js — Derivazione deterministica di chiavi contestuali dalla
 * mnemonica master BIP-39.
 *
 * Uso: a partire dal seed master (64 byte da `mnemonicToSeed`), HKDF-SHA512
 * con un info-string strutturato produce un sub-seed contestuale, da cui
 * libsodium deriva una coppia X25519 (box) e una Ed25519 (sign).
 *
 * Stesso seed + stesso info → STESSE chiavi (determinismo). Info diversi →
 * chiavi non correlabili senza il seed master.
 *
 * Schema info: `nullum-v1:${purpose}:${context}`
 *   purpose = 'c2c'           context = 'primary'      → sigil principale (master c2c)
 *   purpose = 'c2c_derived'   context = 'mio-label'    → sigil derivato c2c (premium)
 *   purpose = 'studio'        context = '<studio_id>'  → sigil contestuale per studio (B2B)
 *   purpose = 'room'          context = '<room_id>'    → identità usa-e-getta per-room (gruppi)
 *
 * Niente roll-your-own: HKDF via WebCrypto (RFC 5869) + sodium primitives.
 */

import sodium from 'libsodium-wrappers-sumo';

const SALT_TEXT = 'nullum-v1';

function te(s) { return new TextEncoder().encode(s); }

/**
 * HKDF-SHA512 via WebCrypto (RFC 5869). Output di 64 byte: i primi 32 servono
 * come seed per X25519 (box), gli ultimi 32 come seed per Ed25519 (sign).
 *
 * @param {Uint8Array} ikm        - seed master (64 byte da mnemonicToSeed)
 * @param {string}     infoString - info-string strutturato (vedi schema sopra)
 * @returns {Promise<Uint8Array>} - 64 byte di output keying material
 */
async function hkdfSha512(ikm, infoString) {
  if (!(ikm instanceof Uint8Array) || ikm.length === 0) {
    throw new Error('keyDerivation: ikm must be a non-empty Uint8Array');
  }
  if (typeof infoString !== 'string' || infoString.length === 0) {
    throw new Error('keyDerivation: infoString must be a non-empty string');
  }
  const baseKey = await crypto.subtle.importKey('raw', ikm, 'HKDF', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits({
    name: 'HKDF',
    hash: 'SHA-512',
    salt: te(SALT_TEXT),
    info: te(infoString),
  }, baseKey, 64 * 8); // 64 byte = 512 bit
  return new Uint8Array(bits);
}

/**
 * Costruisce l'info-string secondo lo schema fisso del prodotto.
 * Validazione stretta: niente caratteri esotici, niente "::" interni che
 * confonderebbero il parsing. Lunghezza massima ragionevole.
 */
export function buildInfo(purpose, context) {
  if (!['c2c', 'c2c_derived', 'studio', 'room'].includes(purpose)) {
    throw new Error(`keyDerivation: purpose invalido '${purpose}'`);
  }
  if (typeof context !== 'string' || context.length === 0 || context.length > 64) {
    throw new Error('keyDerivation: context deve essere stringa 1..64 chars');
  }
  if (!/^[A-Za-z0-9_\-]+$/.test(context)) {
    throw new Error('keyDerivation: context puo contenere solo [A-Za-z0-9_-]');
  }
  return `nullum-v1:${purpose}:${context}`;
}

/**
 * Deriva una coppia X25519 (box) + una Ed25519 (sign) da seed master + info.
 *
 * Output:
 *   {
 *     box:  { publicKey: Uint8Array(32), privateKey: Uint8Array(32) },
 *     sign: { publicKey: Uint8Array(32), privateKey: Uint8Array(64) },
 *     subseed: Uint8Array(64)   // utile per derivare ulteriore materiale chiave
 *   }
 */
export async function deriveContextualKeys(masterSeed, purpose, context) {
  await sodium.ready;
  const info = buildInfo(purpose, context);
  const okm = await hkdfSha512(masterSeed, info);
  const boxSeed  = okm.slice(0, 32);
  const signSeed = okm.slice(32, 64);
  const boxKp  = sodium.crypto_box_seed_keypair(boxSeed);
  const signKp = sodium.crypto_sign_seed_keypair(signSeed);
  return {
    box: {
      publicKey: boxKp.publicKey,
      privateKey: boxKp.privateKey,
    },
    sign: {
      publicKey: signKp.publicKey,
      privateKey: signKp.privateKey,
    },
    subseed: okm,
  };
}

/**
 * Shortcut per il sigil principale C2C.
 */
export function deriveMasterC2C(masterSeed) {
  return deriveContextualKeys(masterSeed, 'c2c', 'primary');
}

/**
 * Shortcut per un sigil derivato C2C (premium).
 * Il context è la label scelta dall'utente, normalizzata.
 */
export function deriveDerivedC2C(masterSeed, label) {
  const safe = String(label || '').toLowerCase().replace(/[^a-z0-9_-]/g, '_').slice(0, 64);
  if (!safe) throw new Error('keyDerivation: label vuota dopo normalizzazione');
  return deriveContextualKeys(masterSeed, 'c2c_derived', safe);
}

/**
 * Shortcut per un sigil contestuale studio (B2B).
 */
export function deriveStudioContext(masterSeed, studioId) {
  return deriveContextualKeys(masterSeed, 'studio', studioId);
}

/**
 * Shortcut per l'identità USA-E-GETTA di una Room (gruppi).
 * Deterministica: stesso (seed, roomId) → stessa identità (stabile DENTRO la
 * room, su ogni device/sessione, senza salvare nulla). RoomId diversi → identità
 * non correlabili (niente intersezione tra room). Il roomId deve rispettare il
 * context: 1..64 char in [A-Za-z0-9_-].
 */
export function deriveRoomContext(masterSeed, roomId) {
  return deriveContextualKeys(masterSeed, 'room', roomId);
}
